/* Il minimo che serve alla scena d'accesso per girare dentro l'applicazione.

   La scena (sonitechLoginScena.js) e' il file del titolare, intatto nei suoi movimenti e nei
   suoi numeri. Gira pero' su un motore di composizione fatto per l'editor — barra di
   riproduzione, pannelli, salvataggi — che qui non serve e porterebbe 55 KB di roba inerte.
   Quindi di quel motore si prende solo cio' che la scena usa davvero:

     Easing, interpolate, animate   copiati VERBATIM dal suo animations-v3.jsx
     useComposition                 lo stesso contratto {T, CUES, authoredTotal}
     Palco                          lo stage 1920x1080 scalato nella finestra

   La differenza, che e' il motivo di tutto: qui il tempo non scorre in cerchio. Si ferma
   quando il riquadro d'accesso e' pronto e aspetta chi deve entrare; alla pressione del
   tasto riparte, salta la compilazione finta e fa l'uscita. */
import React from 'react';

// ── Copiati dal motore del titolare, senza modifiche ────────────────────────
export const Easing = {
  linear: (t) => t,
  easeInQuad: (t) => t * t,
  easeOutQuad: (t) => t * (2 - t),
  easeInOutQuad: (t) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t),
  easeInCubic: (t) => t * t * t,
  easeOutCubic: (t) => (--t) * t * t + 1,
  easeInOutCubic: (t) => (t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1),
  easeInQuart: (t) => t * t * t * t,
  easeOutQuart: (t) => 1 - (--t) * t * t * t,
  easeInOutQuart: (t) => (t < 0.5 ? 8 * t * t * t * t : 1 - 8 * (--t) * t * t * t),
  easeInExpo: (t) => (t === 0 ? 0 : Math.pow(2, 10 * (t - 1))),
  easeOutExpo: (t) => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t)),
  easeInOutExpo: (t) => {
    if (t === 0) return 0;
    if (t === 1) return 1;
    if (t < 0.5) return 0.5 * Math.pow(2, 20 * t - 10);
    return 1 - 0.5 * Math.pow(2, -20 * t + 10);
  },
  easeInSine: (t) => 1 - Math.cos((t * Math.PI) / 2),
  easeOutSine: (t) => Math.sin((t * Math.PI) / 2),
  easeInOutSine: (t) => -(Math.cos(Math.PI * t) - 1) / 2,
  easeOutBack: (t) => {
    const c1 = 1.70158, c3 = c1 + 1;
    return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
  },
  easeInBack: (t) => {
    const c1 = 1.70158, c3 = c1 + 1;
    return c3 * t * t * t - c1 * t * t;
  },
  easeInOutBack: (t) => {
    const c1 = 1.70158, c2 = c1 * 1.525;
    return t < 0.5
      ? (Math.pow(2 * t, 2) * ((c2 + 1) * 2 * t - c2)) / 2
      : (Math.pow(2 * t - 2, 2) * ((c2 + 1) * (t * 2 - 2) + c2) + 2) / 2;
  },
  easeOutElastic: (t) => {
    const c4 = (2 * Math.PI) / 3;
    if (t === 0) return 0;
    if (t === 1) return 1;
    return Math.pow(2, -10 * t) * Math.sin((t * 10 - 0.75) * c4) + 1;
  },
};

export function interpolate(input, output, ease = Easing.linear) {
  return (t) => {
    if (t <= input[0]) return output[0];
    if (t >= input[input.length - 1]) return output[output.length - 1];
    for (let i = 0; i < input.length - 1; i++) {
      if (t >= input[i] && t <= input[i + 1]) {
        const span = input[i + 1] - input[i];
        const local = span === 0 ? 0 : (t - input[i]) / span;
        const easeFn = Array.isArray(ease) ? (ease[i] || Easing.linear) : ease;
        const eased = easeFn(local);
        return output[i] + (output[i + 1] - output[i]) * eased;
      }
    }
    return output[output.length - 1];
  };
}

export function animate({ from = 0, to = 1, start = 0, end = 1, ease = Easing.easeInOutCubic }) {
  return (t) => {
    if (t <= start) return from;
    if (t >= end) return to;
    const local = (t - start) / (end - start);
    return from + (to - from) * ease(local);
  };
}

// ── Le scene, come le ha scritte lui nel documento ──────────────────────────
export const SCENE = [
  { nome: 'Reveal', dur: 2.4 },
  { nome: 'Accento', dur: 1.2 },
  { nome: 'Hold', dur: 2.4 },
  { nome: 'Trasformazione', dur: 1.8 },
  { nome: 'Simbolo', dur: 1.4 },
  { nome: 'Login', dur: 1.3 },
  { nome: 'Accesso', dur: 3.3 },
  { nome: 'Uscita', dur: 1 },
];

/** CUES: l'istante in cui comincia ogni scena (la somma delle precedenti). */
export const CUES = (() => {
  const t = {};
  let somma = 0;
  for (const s of SCENE) { t[s.nome] = +somma.toFixed(3); somma += s.dur; }
  t.Totale = +somma.toFixed(3);
  return t;
})();

/** Quando il riquadro d'accesso e' formato e si aspetta chi deve entrare. */
export const T_ATTESA = CUES.Accesso;          // 10.5s: fine della scena "Login"
/** Da dove riprende alla pressione del tasto: si salta la compilazione finta. */
export const T_USCITA = CUES.Uscita;           // 13.8s
export const T_FINE = CUES.Totale;             // 14.8s

const Composizione = React.createContext(null);

export function useComposition() {
  const ctx = React.useContext(Composizione);
  if (!ctx) throw new Error('useComposition() va usato dentro <Palco>');
  return ctx;
}

/* Il tempo della scena. Avanza fino a `fermaA`; quando `corri` torna vero riparte da
   `riprendiDa`. `onFine` scatta una volta sola, alla fine della corsa. */
export function useTempo({ fermaA, riprendiDa, corri, onFine }) {
  const [T, setT] = React.useState(0);
  const raf = React.useRef(0);
  const finito = React.useRef(false);
  const corriRef = React.useRef(corri);
  const onFineRef = React.useRef(onFine);
  corriRef.current = corri;
  onFineRef.current = onFine;

  React.useEffect(() => {
    let precedente = null;
    let tempo = 0;
    let inCorsa = true;
    const passo = (ora) => {
      if (precedente == null) precedente = ora;
      const dt = Math.min(0.05, (ora - precedente) / 1000);   // schede in background: niente salti
      precedente = ora;
      if (corriRef.current && tempo < fermaA) tempo = fermaA;  // si entra: salta all'uscita
      if (corriRef.current && tempo < riprendiDa) tempo = riprendiDa;
      const limite = corriRef.current ? T_FINE : fermaA;
      if (tempo < limite) tempo = Math.min(limite, tempo + dt);
      setT(tempo);
      if (corriRef.current && tempo >= T_FINE && !finito.current) {
        finito.current = true;
        if (onFineRef.current) onFineRef.current();
      }
      if (inCorsa) raf.current = requestAnimationFrame(passo);
    };
    raf.current = requestAnimationFrame(passo);
    return () => { inCorsa = false; cancelAnimationFrame(raf.current); };
  }, [fermaA, riprendiDa]);

  return T;
}

/* Il palco: la scena e' disegnata su 1920x1080 e qui viene scalata per stare nella
   finestra, come faceva lo Stage del motore. */
export function Palco({ T, larghezza = 1920, altezza = 1080, sfondo = '#100F1E', children }) {
  const ref = React.useRef(null);
  const [scala, setScala] = React.useState(1);

  React.useEffect(() => {
    const misura = () => {
      const el = ref.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      if (r.width && r.height) setScala(Math.min(r.width / larghezza, r.height / altezza));
    };
    misura();
    window.addEventListener('resize', misura);
    let ro = null;
    if (typeof ResizeObserver !== 'undefined' && ref.current) {
      ro = new ResizeObserver(misura);
      ro.observe(ref.current);
    }
    return () => { window.removeEventListener('resize', misura); if (ro) ro.disconnect(); };
  }, [larghezza, altezza]);

  return (
    <div ref={ref} style={{ position: 'absolute', inset: 0, background: sfondo, overflow: 'hidden' }}>
      <div style={{
        position: 'absolute', left: '50%', top: '50%', width: larghezza, height: altezza,
        transform: `translate(-50%, -50%) scale(${scala})`, transformOrigin: 'center center',
      }}>
        <Composizione.Provider value={{ T, CUES, authoredTotal: T_FINE, scala }}>
          {children}
        </Composizione.Provider>
      </div>
    </div>
  );
}
